import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Apollo, gql } from 'apollo-angular';
import { AuthService } from '../../auth/auth.service';
import { ToastService } from '../../shared/toast/toast.service';

const SITE_ID_FOR_URL = gql`
  query SiteIdForUrl($url: String!) {
    site(url: $url, categoryId: "") {
      _id
    }
  }
`;

const LIST_USERS = gql`
  query ListSiteUsers($siteId: String!) {
    users(siteId: $siteId) {
      _id
      email
      name
      role
      status
      receiveDailyDigest
      invitedAt
      createdAt
    }
  }
`;

const INVITE_USER = gql`
  mutation InviteSiteUser($input: InviteUserInput!) {
    inviteUser(input: $input) {
      _id
    }
  }
`;

const RESEND_INVITE = gql`
  mutation ResendSiteInvite($userId: String!) {
    resendInvite(userId: $userId) {
      _id
    }
  }
`;

const DEACTIVATE_USER = gql`
  mutation DeactivateSiteUser($userId: String!) {
    deactivateUser(userId: $userId) {
      _id
      status
    }
  }
`;

const REACTIVATE_USER = gql`
  mutation ReactivateSiteUser($userId: String!) {
    reactivateUser(userId: $userId) {
      _id
      status
    }
  }
`;

const DELETE_USER = gql`
  mutation DeleteSiteUser($userId: String!) {
    deleteUser(userId: $userId) {
      _id
    }
  }
`;

const UPDATE_USER_ROLE = gql`
  mutation UpdateSiteUserRole($input: UpdateUserRoleInput!) {
    updateUserRole(input: $input) {
      _id
      role
    }
  }
`;

const TOGGLE_DAILY_DIGEST = gql`
  mutation ToggleDailyDigest($userId: String!, $receive: Boolean!) {
    toggleDailyDigest(userId: $userId, receive: $receive) {
      _id
      receiveDailyDigest
    }
  }
`;

interface PortalUser {
  _id: string;
  email: string;
  name?: string | null;
  role: 'PLATFORM_ADMIN' | 'SITE_ADMIN' | 'SITE_MEMBER';
  status: 'INVITED' | 'ACTIVE' | 'DISABLED';
  receiveDailyDigest: boolean;
  invitedAt?: string | null;
  createdAt: string;
}

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  standalone: false,
})
export class UsersComponent implements OnInit {
  siteUrl = '';
  siteId = '';
  users: PortalUser[] = [];
  loading = false;

  showInviteModal = false;
  showDeactivateModal = false;
  userToDeactivate: PortalUser | null = null;
  showDeleteModal = false;
  userToDelete: PortalUser | null = null;
  inviteForm: FormGroup;
  isInviting = false;

  readonly roles: { value: PortalUser['role']; label: string }[] = [
    { value: 'SITE_MEMBER', label: 'Member' },
    { value: 'SITE_ADMIN', label: 'Site admin' },
  ];

  constructor(
    private route: ActivatedRoute,
    private apollo: Apollo,
    private fb: FormBuilder,
    private toast: ToastService,
    public auth: AuthService,
  ) {
    this.inviteForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      role: ['SITE_MEMBER', Validators.required],
    });
  }

  ngOnInit() {
    this.siteUrl = this.route.parent?.snapshot.params['url'] || '';
    this.resolveSiteId();
  }

  private resolveSiteId() {
    if (!this.siteUrl) return;
    this.apollo
      .query<{ site: { _id: string } }>({
        query: SITE_ID_FOR_URL,
        variables: { url: this.siteUrl },
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: ({ data }) => {
          this.siteId = data?.site?._id ?? '';
          if (this.siteId) this.loadUsers();
        },
        error: (err) => this.toast.error(err?.message || 'Could not load site.'),
      });
  }

  loadUsers() {
    this.loading = true;
    this.apollo
      .query<{ users: PortalUser[] }>({
        query: LIST_USERS,
        variables: { siteId: this.siteId },
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: ({ data }) => {
          this.users = data?.users ?? [];
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.toast.error(err?.message || 'Could not load users.');
        },
      });
  }

  openInviteModal() {
    this.inviteForm.reset({ email: '', role: 'SITE_MEMBER' });
    this.showInviteModal = true;
  }

  closeInviteModal() {
    this.showInviteModal = false;
  }

  submitInvite() {
    if (this.inviteForm.invalid || this.isInviting) return;
    this.isInviting = true;
    const { email, role } = this.inviteForm.value;
    this.apollo
      .mutate({
        mutation: INVITE_USER,
        variables: { input: { email, role, siteIds: [this.siteId] } },
      })
      .subscribe({
        next: () => {
          this.isInviting = false;
          this.showInviteModal = false;
          this.toast.success(`Invite sent to ${email}.`);
          this.loadUsers();
        },
        error: (err) => {
          this.isInviting = false;
          this.toast.error(err?.message || 'Could not send invite.');
        },
      });
  }

  resendInvite(user: PortalUser) {
    this.apollo
      .mutate({ mutation: RESEND_INVITE, variables: { userId: user._id } })
      .subscribe({
        next: () => {
          this.toast.success(`Invite resent to ${user.email}.`);
          this.loadUsers();
        },
        error: (err) => this.toast.error(err?.message || 'Could not resend invite.'),
      });
  }

  deactivateUser(user: PortalUser) {
    this.userToDeactivate = user;
    this.showDeactivateModal = true;
  }

  confirmDeactivate() {
    const user = this.userToDeactivate;
    if (!user) return;
    this.showDeactivateModal = false;
    this.userToDeactivate = null;
    this.apollo
      .mutate({ mutation: DEACTIVATE_USER, variables: { userId: user._id } })
      .subscribe({
        next: () => {
          this.toast.success(`${user.email} deactivated.`);
          this.loadUsers();
        },
        error: (err) => this.toast.error(err?.message || 'Could not deactivate user.'),
      });
  }

  cancelDeactivate() {
    this.showDeactivateModal = false;
    this.userToDeactivate = null;
  }

  reactivateUser(user: PortalUser) {
    this.apollo
      .mutate({ mutation: REACTIVATE_USER, variables: { userId: user._id } })
      .subscribe({
        next: () => {
          this.toast.success(`${user.email} reactivated.`);
          this.loadUsers();
        },
        error: (err) => this.toast.error(err?.message || 'Could not reactivate user.'),
      });
  }

  deleteUser(user: PortalUser) {
    this.userToDelete = user;
    this.showDeleteModal = true;
  }

  confirmDeleteUser() {
    const user = this.userToDelete;
    if (!user) return;
    this.showDeleteModal = false;
    this.userToDelete = null;
    this.apollo
      .mutate({ mutation: DELETE_USER, variables: { userId: user._id } })
      .subscribe({
        next: () => {
          this.toast.success(`${user.email} deleted.`);
          this.loadUsers();
        },
        error: (err) => this.toast.error(err?.message || 'Could not delete user.'),
      });
  }

  cancelDeleteUser() {
    this.showDeleteModal = false;
    this.userToDelete = null;
  }

  toggleDailyDigest(user: PortalUser) {
    const newValue = !user.receiveDailyDigest;
    this.apollo
      .mutate({ mutation: TOGGLE_DAILY_DIGEST, variables: { userId: user._id, receive: newValue } })
      .subscribe({
        next: () => {
          this.users = this.users.map((u) =>
            u._id === user._id ? { ...u, receiveDailyDigest: newValue } : u,
          );
          this.toast.success(`Daily digest ${newValue ? 'enabled' : 'disabled'} for ${user.email}.`);
        },
        error: (err) => this.toast.error(err?.message || 'Could not update digest setting.'),
      });
  }

  statusLabel(status: PortalUser['status']): string {
    switch (status) {
      case 'INVITED':
        return 'Pending invite';
      case 'ACTIVE':
        return 'Active';
      case 'DISABLED':
        return 'Disabled';
    }
  }

  roleLabel(role: PortalUser['role']): string {
    switch (role) {
      case 'PLATFORM_ADMIN':
        return 'Platform admin';
      case 'SITE_ADMIN':
        return 'Site admin';
      case 'SITE_MEMBER':
        return 'Member';
    }
  }

  updateRole(user: PortalUser, event: Event) {
    const role = (event.target as HTMLSelectElement).value as PortalUser['role'];
    this.apollo.mutate({ mutation: UPDATE_USER_ROLE, variables: { input: { userId: user._id, role } } }).subscribe({
      next: () => {
        user.role = role;
        this.toast.success(`Role updated to ${this.roleLabel(role)}.`);
      },
      error: (err) => this.toast.error(err?.message || 'Could not update role.'),
    });
  }
}
